import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Box,
  Divider,
  Avatar,
} from '@mui/material';
import { Close as CloseIcon, DragIndicator } from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToVerticalAxis,
  restrictToParentElement,
} from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDispatch } from 'react-redux';
// import { useTranslation } from 'react-i18next';
import { reorderProducts } from 'redux/actions/Menu/products';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { showPriceRange } from 'utils/menu';

const SortableItem = ({ product }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: product._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      sx={{
        mb: 1,
        backgroundColor: isDragging ? '#e3f2fd' : '#f5f5f5',
        borderRadius: 1,
        border: '1px solid #e0e0e0',
        '&:hover': {
          backgroundColor: '#eeeeee',
        },
        cursor: isDragging ? 'grabbing' : 'grab',
      }}
      {...attributes}
      {...listeners}
    >
      <ListItemIcon>
        <DragIndicator sx={{ color: '#9E9EAD' }} />
      </ListItemIcon>
      <ListItemIcon sx={{ minWidth: 'auto', mr: 2 }}>
        {product?.image?.url ? (
          <Avatar
            src={`${product?.image?.url}?tr=w-40,h-40`}
            alt={product?.name}
            sx={{
              width: 40,
              height: 40,
              backgroundColor: '#e0e0e0',
            }}
          />
        ) : (
          <Avatar
            sx={{
              width: 40,
              height: 40,
              backgroundColor: '#e0e0e0',
            }}
          >
            <ImageOutlinedIcon sx={{ color: '#323232' }} />
          </Avatar>
        )}
      </ListItemIcon>
      <ListItemText
        primary={
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {product.name}
          </Typography>
        }
        secondary={
          <Typography variant="body2" color="text.secondary">
            {showPriceRange(product)}
          </Typography>
        }
      />
    </ListItem>
  );
};

const ItemSortModal = ({ open, onClose, category, products }) => {
  // const { t } = useTranslation();
  const dispatch = useDispatch();
  const [sortedProducts, setSortedProducts] = useState(products || []);
  const [hasChanges, setHasChanges] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Update sorted products when products prop changes
  React.useEffect(() => {
    if (products) {
      setSortedProducts([...products]);
      setHasChanges(false);
    }
  }, [products, open]);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setSortedProducts((items) => {
        const oldIndex = items.findIndex((item) => item._id === active.id);
        const newIndex = items.findIndex((item) => item._id === over.id);

        const newOrder = arrayMove(items, oldIndex, newIndex);
        setHasChanges(true);
        return newOrder;
      });
    }
  }, []);

  const handleSave = async () => {
    if (hasChanges && category) {
      const productIds = sortedProducts.map((product) => product._id);
      try {
        await dispatch(reorderProducts(productIds, category._id));
        setHasChanges(false);
        onClose();
      } catch (error) {
        console.error('Failed to save product order:', error);
      }
    } else {
      onClose();
    }
  };

  const handleCancel = () => {
    setSortedProducts([...products]);
    setHasChanges(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '400px',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Box>
          <Typography variant="h6" component="div">
            Sort Items
          </Typography>
          {category && (
            <Typography variant="body2" color="text.secondary">
              in {category.name}
            </Typography>
          )}
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleCancel}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2, overflowX: 'hidden' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Drag and drop items to reorder them within this category. Changes will
          be saved when you click &quot;Save&quot;.
        </Typography>

        {sortedProducts.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          >
            <SortableContext
              items={sortedProducts.map((product) => product._id)}
              strategy={verticalListSortingStrategy}
            >
              <List sx={{ padding: 0, overflowX: 'hidden' }}>
                {sortedProducts.map((product) => (
                  <SortableItem key={product._id} product={product} />
                ))}
              </List>
            </SortableContext>
          </DndContext>
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px',
            }}
          >
            <Typography variant="body1" color="text.secondary">
              No items to sort in this category
            </Typography>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleCancel} variant="outlined" sx={{ mr: 1 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!hasChanges}
          sx={{
            backgroundColor: hasChanges ? '#1976d2' : undefined,
          }}
        >
          {'SAVE'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ItemSortModal;
